import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { XCircle, RefreshCw, Banknote, Wallet, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useData } from "../context/DataContext";
import { resolveImageUrl } from "../admin/ImageUpload";

const fmtEur = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;
const MODE_LABEL = { card: "Carte bancaire", monthly: "Prélèvement SEPA 8x" };
const CTX_KEY = "elan_stripe_retry";

const PaymentCancel = () => {
  const { settings } = useData();
  const navigate = useNavigate();
  const logo = resolveImageUrl(settings?.logo);

  const [ctx, setCtx] = useState(null);
  const [busy, setBusy] = useState(null); // 'retry' | 'cheque' | 'especes' | null

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(CTX_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Only trust context saved within the last hour
        if (parsed && parsed.enrollmentIds?.length && Date.now() - (parsed.savedAt || 0) < 60 * 60 * 1000) {
          setCtx(parsed);
        }
      }
    } catch { /* ignore parse errors */ }
  }, []);

  const retryStripe = async () => {
    if (!ctx) return;
    setBusy("retry");
    try {
      const { data } = await api.post("/payments/checkout", {
        enrollmentIds: ctx.enrollmentIds,
        originUrl: window.location.origin,
        mode: ctx.mode,
      });
      // Refresh the timestamp
      try { sessionStorage.setItem(CTX_KEY, JSON.stringify({ ...ctx, savedAt: Date.now() })); } catch { /* ignore */ }
      window.location.href = data.url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" && detail ? detail : "Impossible de relancer le paiement, réessayez dans un instant.");
      setBusy(null);
    }
  };

  const switchTo = async (paymentMode) => {
    if (!ctx) return;
    setBusy(paymentMode);
    try {
      await api.post("/payments/switch-to-manual", {
        enrollmentIds: ctx.enrollmentIds,
        paymentMode,
      });
      try { sessionStorage.removeItem(CTX_KEY); } catch { /* ignore */ }
      toast.success(paymentMode === "cheque" ? "Réglé par chèque. À déposer au forum." : "Réglé en espèces. À déposer au forum.");
      // If the retry came from the family portal, send them back there
      const returnTo = ctx.returnTo || "/inscription/paiement-succes?manual=1";
      navigate(returnTo, { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" && detail ? detail : "Erreur, veuillez réessayer.");
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6 py-12">
      <div className="max-w-lg w-full bg-white rounded-3xl shadow-xl p-8 sm:p-10 text-center">
        {logo && <img src={logo} alt="L'Élan Danse" className="h-14 mx-auto mb-5 object-contain" />}
        <XCircle size={56} className="mx-auto text-amber-500" />
        <h1 className="font-serif-display text-2xl text-stone-800 mt-4">Paiement non finalisé</h1>
        <p className="text-stone-600 mt-2 text-sm">
          Aucun montant n&apos;a été débité. Votre inscription est <strong>en attente</strong> — vous pouvez réessayer maintenant ou choisir un autre mode.
        </p>

        {ctx ? (
          <div className="mt-6 bg-cream rounded-2xl p-4 text-left text-sm space-y-1.5" data-testid="payment-cancel-context">
            {ctx.orderNumber && (
              <div className="flex justify-between"><span className="text-stone-500">N° commande</span><span className="text-stone-800 font-mono font-medium">{ctx.orderNumber}</span></div>
            )}
            <div className="flex justify-between"><span className="text-stone-500">Mode initial</span><span className="text-stone-800">{MODE_LABEL[ctx.mode] || ctx.mode}</span></div>
            {typeof ctx.total === "number" && (
              <div className="flex justify-between"><span className="text-stone-500">Montant</span><span className="text-stone-800 font-medium">{fmtEur(ctx.total)}</span></div>
            )}
          </div>
        ) : null}

        {ctx ? (
          <div className="mt-6 space-y-3">
            <button
              onClick={retryStripe}
              disabled={!!busy}
              data-testid="payment-cancel-retry-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-coral text-white text-sm tracking-widest uppercase font-serif-display disabled:opacity-50 hover:brightness-105 transition"
            >
              {busy === "retry" ? <Loader2 size={16} className="animate-spin"/> : <RefreshCw size={16}/>}
              Réessayer le paiement Stripe
            </button>

            <div className="flex items-center gap-2 text-xs text-stone-400 my-2">
              <div className="flex-1 h-px bg-stone-200"/>
              <span>ou basculer sur un règlement au forum</span>
              <div className="flex-1 h-px bg-stone-200"/>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                onClick={() => switchTo("cheque")}
                disabled={!!busy}
                data-testid="payment-cancel-switch-cheque-btn"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-800 text-sm disabled:opacity-50 transition"
              >
                {busy === "cheque" ? <Loader2 size={14} className="animate-spin"/> : <Banknote size={14}/>}
                Chèque
              </button>
              <button
                onClick={() => switchTo("especes")}
                disabled={!!busy}
                data-testid="payment-cancel-switch-especes-btn"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-800 text-sm disabled:opacity-50 transition"
              >
                {busy === "especes" ? <Loader2 size={14} className="animate-spin"/> : <Wallet size={14}/>}
                Espèces
              </button>
            </div>
            <p className="text-xs text-stone-400 mt-2">
              En choisissant chèque ou espèces, votre inscription est conservée. Merci d&apos;apporter votre règlement au prochain forum ou lors d&apos;une permanence.
            </p>
          </div>
        ) : (
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/inscription" className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-coral text-white text-sm tracking-widest uppercase font-serif-display">
              <ArrowLeft size={14}/> Reprendre l&apos;inscription
            </Link>
            <Link to="/" className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-stone-100 text-stone-700 text-sm tracking-widest uppercase">Retour au site</Link>
          </div>
        )}

        <div className="mt-6 text-center">
          <Link to="/" className="text-xs text-stone-500 hover:text-deep-rose">Retour au site</Link>
        </div>
      </div>
    </div>
  );
};

export default PaymentCancel;
